import React, { FC, useMemo, useState, useContext } from "react";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { ShiftEditableItemCard } from "@/app/application/admin-menu/settings/ShiftEditableItemCard";
import { ShiftSettingsSection } from "@/app/application/admin-menu/settings/ShiftSettingsSection";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { Spinner } from "@/shared/ui/Spinner";
import {
	Add as AddIcon,
	FormatColorReset as FormatColorResetIcon,
	Groups as GroupsIcon,
} from "@mui/icons-material";
import {
	Box,
	Button,
	IconButton,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { ShiftName } from "@/model/Application/Settings/SettingsResponseTypes";

import {
	useMutateCreateShiftCrew,
	useMutateDeleteShiftCrew,
	useMutateUpdateShiftCrew,
} from "@/service/Settings/Mutations/ShiftSettingsMutations";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{6})$/;

const toStringValue = (value: unknown): string => {
	if (typeof value === "string") return value;
	if (
		typeof value === "number" ||
		typeof value === "boolean" ||
		typeof value === "bigint"
	) {
		return String(value);
	}

	return "";
};

const normalizeHexColor = (value: unknown): string => {
	const s = toStringValue(value).trim();
	return HEX_COLOR_REGEX.test(s) ? s : "";
};

const toColorInputValue = (value: unknown): string => {
	return normalizeHexColor(value) || "#000000";
};

interface ShiftCrewSettingsProps {
	data: unknown[];
}

type ShiftCrewRow = {
	Id: number;
	id: number;
	Name: string;
	Color: string;
	StartTime?: string;
	EndTime?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;

const toNumberValue = (value: unknown): number | null => {
	if (typeof value === "number" && Number.isFinite(value)) return value;
	if (typeof value === "string" && value.trim() !== "") {
		const parsed = Number(value);
		if (Number.isFinite(parsed)) return parsed;
	}
	return null;
};

const normalizeShiftCrewRow = (value: unknown): ShiftCrewRow | null => {
	if (!isRecord(value)) return null;
	const id = toNumberValue(value.Id ?? value.id);
	if (id === null) return null;
	return {
		Id: id,
		id,
		Name: toStringValue(value.Name ?? value.name),
		Color: normalizeHexColor(value.Color ?? value.color),
		StartTime: toStringValue(value.StartTime ?? value.startTime) || undefined,
		EndTime: toStringValue(value.EndTime ?? value.endTime) || undefined,
	};
};

export const ShiftCrewSettings: FC<ShiftCrewSettingsProps> = ({ data }) => {
	const translation: Language | null = useContext(TranslationContext);
	const { createShiftCrew } = useMutateCreateShiftCrew();
	const { updateShiftCrew } = useMutateUpdateShiftCrew();
	const { deleteShiftCrew } = useMutateDeleteShiftCrew();

	const [editingId, setEditingId] = useState<number | "new" | null>(null);
	const [saving, setSaving] = useState(false);

	const rows = useMemo(() => {
		return (data ?? [])
			.map(normalizeShiftCrewRow)
			.filter((row): row is ShiftCrewRow => row !== null);
	}, [data]);

	const handleCreate = async (item: { Name: string; Color: string }) => {
		if (!item.Name?.trim()) return;
		setSaving(true);
		try {
			await createShiftCrew({
				name: item.Name.trim(),
				color: normalizeHexColor(item.Color),
			});
			void AlertRequest.success();
			setEditingId(null);
		} catch (e) {
			console.error(e);
			void AlertRequest.failed();
		} finally {
			setSaving(false);
		}
	};

	const handleUpdate = async (
		row: ShiftCrewRow,
		updates: { Name?: string; Color?: string },
	) => {
		const name = (updates.Name ?? row.Name ?? "").trim();
		if (!name) return;
		setSaving(true);
		try {
			const updateRequest: ShiftName = {
				Id: row.Id,
				Name: name,
				StartTime: row.StartTime ?? "",
				EndTime: row.EndTime ?? "",
				Color: normalizeHexColor(updates.Color ?? row.Color),
			};
			await updateShiftCrew(updateRequest);
			void AlertRequest.success();
			setEditingId(null);
		} catch (e) {
			console.error(e);
			void AlertRequest.failed();
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (id: number) => {
		try {
			await AlertRequest.confirmAsync(deleteShiftCrew, id, "this crew name");
			setEditingId(null);
		} catch (e) {
			console.error(e);
			void AlertRequest.failed();
		}
	};

	const handleAddClick = () => setEditingId("new");

	if (!data) return <Spinner text={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftCrewSettings.L165C34.LoadingSettings", "Loading settings")} />;

	return (
		<ShiftSettingsSection
			title={<Translate String="Shift.Event.CrewName">Crew Name</Translate>}
			icon={<GroupsIcon />}
			action={
				<Button
					variant="outlined"
					color="inherit"
					startIcon={<AddIcon />}
					onClick={handleAddClick}
					sx={{
						height: 32,
						minHeight: 32,
						px: 1.5,
						textTransform: "none",
						fontWeight: 600,
						borderColor: "divider",
						bgcolor: "background.paper",
						boxShadow: "none",
						whiteSpace: "nowrap",
					}}
				>
					<Translate String="Settings.Shift.Crew.AddShiftCrew">
						Add Crew
					</Translate>
				</Button>
			}
			sx={{ width: "100%", height: "100%" }}
			headerSx={{
				alignItems: "center",
				pb: 2,
				borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
				mb: 2.5,
			}}
		>
			<Stack spacing={1.5}>
				{rows.map((row) => (
					<ShiftCrewItem
						key={row.Id}
						row={row}
						isEditing={editingId === row.Id}
						onStartEdit={() => setEditingId(row.Id)}
						onCancel={() => setEditingId(null)}
						onSave={(updates) => {
							void handleUpdate(row, updates);
						}}
						onDelete={() => {
							void handleDelete(row.Id);
						}}
						saving={saving}
					/>
				))}
				{editingId === "new" && (
					<ShiftCrewEditForm
						initial={{ Name: "", Color: "" }}
						onSave={(item) => {
							void handleCreate(item);
						}}
						onCancel={() => setEditingId(null)}
						saving={saving}
						isNew
					/>
				)}
				{rows.length === 0 && editingId !== "new" && (
					<Box py={4} textAlign="center" sx={{ color: "text.secondary" }}>
						<Typography variant="body2">
							No crew names yet. Add one to get started.
						</Typography>
					</Box>
				)}
			</Stack>
		</ShiftSettingsSection>
	);
};

interface ShiftCrewItemProps {
	row: { Id: number; Name: string; Color: string };
	isEditing: boolean;
	onStartEdit: () => void;
	onCancel: () => void;
	onSave: (updates: { Name?: string; Color?: string }) => void;
	onDelete: () => void;
	saving: boolean;
}

const ShiftCrewItem: FC<ShiftCrewItemProps> = ({
	row,
	isEditing,
	onStartEdit,
	onCancel,
	onSave,
	onDelete,
	saving,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const color = normalizeHexColor(row.Color);
	const hasColor = Boolean(color);
	const [editState, setEditState] = useState({
		Name: row.Name ?? "",
		Color: normalizeHexColor(row.Color),
	});

	React.useEffect(() => {
		if (isEditing) {
			setEditState({
				Name: row.Name ?? "",
				Color: normalizeHexColor(row.Color),
			});
		}
	}, [isEditing, row.Name, row.Color]);

	const handleSave = () => onSave(editState);
	const editColor = normalizeHexColor(editState.Color);
	const hasEditColor = Boolean(editColor);

	const viewContent = (
		<Stack direction="row" spacing={2} alignItems="center">
			<Box
				sx={{
					width: 20,
					height: 20,
					borderRadius: 1,
					bgcolor: hasColor ? color : "transparent",
					border: (theme) => {
						const borderColor =
							theme.palette.mode === "dark"
								? "rgba(255,255,255,0.28)"
								: "rgba(0,0,0,0.25)";
						return `1px ${hasColor ? "solid" : "dashed"} ${borderColor}`;
					},
					backgroundImage: hasColor
						? "none"
						: "repeating-conic-gradient(rgba(0,0,0,0.12) 0% 25%, transparent 0% 50%)",
					backgroundSize: hasColor ? undefined : "6px 6px",
					flexShrink: 0,
				}}
			/>
			<Typography variant="body1" fontWeight={500}>
				{row.Name}
			</Typography>
		</Stack>
	);

	const editContent = (
		<Stack
			direction="row"
			spacing={2}
			alignItems="center"
			sx={{ width: "100%" }}
		>
			<Box
				sx={{
					position: "relative",
					width: 36,
					height: 36,
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: 1,
					overflow: "hidden",
					cursor: "pointer",
					flexShrink: 0,
					bgcolor: editColor || "transparent",
					backgroundImage: hasEditColor
						? "none"
						: "repeating-conic-gradient(rgba(0,0,0,0.12) 0% 25%, transparent 0% 50%)",
					backgroundSize: hasEditColor ? undefined : "8px 8px",
				}}
			>
				<input
					type="color"
					value={toColorInputValue(editState.Color)}
					onChange={(e) =>
						setEditState((s) => ({ ...s, Color: e.target.value }))
					}
					style={{
						position: "absolute",
						inset: 0,
						opacity: 0,
						cursor: "pointer",
						width: "100%",
						height: "100%",
						border: "none",
						padding: 0,
					}}
				/>
			</Box>
			<Tooltip title={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftCrewSettings.L352C19.ClearColor", "Clear color")}>
				<span>
					<IconButton
						size="small"
						onClick={() => setEditState((s) => ({ ...s, Color: "" }))}
						disabled={!normalizeHexColor(editState.Color)}
						sx={{ color: "text.secondary" }}
						aria-label={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftCrewSettings.L359C18.ClearColor", "Clear color")}
					>
						<FormatColorResetIcon fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
			<TextField
				autoFocus
				fullWidth
				size="small"
				value={editState.Name}
				onChange={(e) => setEditState((s) => ({ ...s, Name: e.target.value }))}
				onKeyDown={(e) => {
					if (e.key === "Enter") handleSave();
					if (e.key === "Escape") onCancel();
				}}
				placeholder={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftCrewSettings.L375C17.CrewName", "Crew name")}
				sx={{
					"& .MuiOutlinedInput-root": {
						bgcolor: (theme: { palette: { mode: string } }) =>
							theme.palette.mode === "dark"
								? "rgba(0,0,0,0.2)"
								: "rgba(0,0,0,0.04)",
						"& fieldset": { borderColor: "transparent" },
					},
				}}
			/>
		</Stack>
	);

	return (
		<ShiftEditableItemCard
			isEditing={isEditing}
			onEdit={onStartEdit}
			onSave={handleSave}
			onCancel={onCancel}
			onDelete={onDelete}
			viewContent={viewContent}
			editContent={editContent}
			saving={saving}
		/>
	);
};

interface ShiftCrewEditFormProps {
	initial: { Name: string; Color: string };
	onSave: (item: { Name: string; Color: string }) => void;
	onCancel: () => void;
	saving: boolean;
	isNew: boolean;
}

const ShiftCrewEditForm: FC<ShiftCrewEditFormProps> = ({
	initial,
	onSave,
	onCancel,
	saving,
	isNew,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const [state, setState] = useState(initial);
	const normalizedColor = normalizeHexColor(state.Color);
	const hasColor = Boolean(normalizedColor);

	const handleSave = () => onSave(state);

	const editContent = (
		<Stack
			direction="row"
			spacing={2}
			alignItems="center"
			sx={{ width: "100%" }}
		>
			<Box
				sx={{
					position: "relative",
					width: 36,
					height: 36,
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: 1,
					overflow: "hidden",
					cursor: "pointer",
					flexShrink: 0,
					bgcolor: hasColor ? normalizedColor : "transparent",
					backgroundImage: hasColor
						? "none"
						: "repeating-conic-gradient(rgba(0,0,0,0.12) 0% 25%, transparent 0% 50%)",
					backgroundSize: hasColor ? undefined : "8px 8px",
				}}
			>
				<input
					type="color"
					value={toColorInputValue(state.Color)}
					onChange={(e) => setState((s) => ({ ...s, Color: e.target.value }))}
					style={{
						position: "absolute",
						inset: 0,
						opacity: 0,
						cursor: "pointer",
						width: "100%",
						height: "100%",
						border: "none",
						padding: 0,
					}}
				/>
			</Box>
			<Tooltip title={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftCrewSettings.L465C19.ClearColor", "Clear color")}>
				<span>
					<IconButton
						size="small"
						onClick={() => setState((s) => ({ ...s, Color: "" }))}
						disabled={!hasColor}
						sx={{ color: "text.secondary" }}
						aria-label={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftCrewSettings.L472C18.ClearColor", "Clear color")}
					>
						<FormatColorResetIcon fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
			<TextField
				autoFocus
				fullWidth
				size="small"
				value={state.Name}
				onChange={(e) => setState((s) => ({ ...s, Name: e.target.value }))}
				onKeyDown={(e) => {
					if (e.key === "Enter") handleSave();
					if (e.key === "Escape") onCancel();
				}}
				placeholder={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftCrewSettings.L488C17.CrewName", "Crew name")}
				sx={{
					"& .MuiOutlinedInput-root": {
						bgcolor: (theme: { palette: { mode: string } }) =>
							theme.palette.mode === "dark"
								? "rgba(0,0,0,0.2)"
								: "rgba(0,0,0,0.04)",
						"& fieldset": { borderColor: "transparent" },
					},
				}}
			/>
		</Stack>
	);

	return (
		<ShiftEditableItemCard
			isEditing
			onEdit={() => {}}
			onSave={handleSave}
			onCancel={onCancel}
			onDelete={() => {}}
			viewContent={null}
			editContent={editContent}
			isNew={isNew}
			saving={saving}
		/>
	);
};
